// src/workflow/components/FormRenderer.jsx
import React from "react";
import {
  Box, Button, FormControl, FormErrorMessage, FormLabel,
  HStack, Input, NumberInput, NumberInputField, Switch, Text, Stack
} from "@chakra-ui/react";

/* ---------- helpers ---------- */
const typeOf = (v) => {
  if (v === null || v === undefined) return "null";
  if (Array.isArray(v)) return "array";
  const t = typeof v;
  if (t === "number" || t === "string" || t === "boolean") return t;
  if (t === "object") return "object";
  return "unknown";
};

/* ---------- primitive field ---------- */
const PrimitiveField = ({ label, value, onChange, readOnly }) => {
  const id = React.useId();
  const t = typeOf(value);
  const ro = !!readOnly;

  switch (t) {
    case "string":
      return (
        <FormControl>
          {label && <FormLabel htmlFor={id}>{label}</FormLabel>}
          <Input id={id} value={value ?? ""} onChange={(e) => onChange(e.target.value)} isReadOnly={ro} />
        </FormControl>
      );
    case "number":
      return (
        <FormControl>
          {label && <FormLabel htmlFor={id}>{label}</FormLabel>}
          <NumberInput value={Number.isFinite(value) ? value : 0} onChange={(_, n) => onChange(n)} isReadOnly={ro} isDisabled={ro}>
            <NumberInputField id={id} />
          </NumberInput>
        </FormControl>
      );
    case "boolean":
      return (
        <FormControl display="flex" alignItems="center">
          {label && <FormLabel htmlFor={id} mb="0">{label}</FormLabel>}
          <Switch id={id} isChecked={!!value} onChange={(e) => onChange(e.target.checked)} isReadOnly={ro} isDisabled={ro} />
        </FormControl>
      );
    case "null":
      return (
        <FormControl>
          {label && <FormLabel htmlFor={id}>{label}</FormLabel>}
          <Input id={id} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder="null" isReadOnly={ro} />
        </FormControl>
      );
    default:
      return (
        <FormControl isInvalid>
          {label && <FormLabel>{label}</FormLabel>}
          <FormErrorMessage>Unsupported primitive type.</FormErrorMessage>
        </FormControl>
      );
  }
};

/* ---------- array field ---------- */
const ArrayField = ({ label, value = [], onChange, readOnly }) => {
  const isPrimitiveArray = value.every((v) => ["string", "number", "boolean", "null"].includes(typeOf(v)));

  const addItem = () => {
    const fallback = value.length ? value[value.length - 1] : "";
    onChange([...(value || []), fallback ?? ""]);
  };
  const removeAt = (idx) => onChange(value.filter((_, i) => i !== idx));
  const updateAt = (idx, v) => onChange(value.map((x, i) => (i === idx ? v : x)));

  return (
    <Box borderWidth="1px" borderRadius="lg" p={3}>
      <HStack justify="space-between">
        <FormLabel m={0}>{label ?? "(array)"}</FormLabel>
        {!readOnly && <Button size="xs" onClick={addItem}>Add</Button>}
      </HStack>
      <Stack spacing={3}>
        {value.map((item, idx) => (
          <Box key={idx} borderWidth="1px" borderRadius="md" p={3}>
            <HStack justify="space-between" mb={2}>
              <Text fontSize="sm" color="gray.500">Index {idx}</Text>
              {!readOnly && <Button size="xs" variant="outline" onClick={() => removeAt(idx)}>Remove</Button>}
            </HStack>
            {isPrimitiveArray
              ? <PrimitiveField value={item} onChange={(v) => updateAt(idx, v)} readOnly={readOnly} />
              : <FormRenderer data={item} onChange={(v) => updateAt(idx, v)} readOnly={readOnly} />
            }
          </Box>
        ))}
        {value.length === 0 && <Text color="gray.500">Empty array</Text>}
      </Stack>
    </Box>
  );
};

/* ---------- object field ---------- */
const ObjectField = ({ value = {}, onChange, readOnly }) => {
  const keys = Object.keys(value);
  return (
    <Stack spacing={4}>
      {keys.map((key) => {
        const v = value[key];
        const t = typeOf(v);
        const update = (nv) => onChange({ ...value, [key]: nv });

        if (t === "object") {
          return (
            <Box key={key} borderWidth="1px" borderRadius="lg" p={3}>
              <FormLabel>{key}</FormLabel>
              <FormRenderer data={v} onChange={update} readOnly={readOnly} />
            </Box>
          );
        }
        if (t === "array") {
          return <ArrayField key={key} label={key} value={v} onChange={update} readOnly={readOnly} />;
        }
        return <PrimitiveField key={key} label={key} value={v} onChange={update} readOnly={readOnly} />;
      })}
      {keys.length === 0 && <Text color="gray.500">Empty object</Text>}
    </Stack>
  );
};

/* ---------- exported recursive renderer ---------- */
const FormRenderer = ({ data, onChange, readOnly }) => {
  const t = typeOf(data);
  if (t === "object") return <ObjectField value={data} onChange={onChange} readOnly={readOnly} />;
  if (t === "array") return <ArrayField value={data} onChange={onChange} readOnly={readOnly} />;
  return <PrimitiveField value={data} onChange={onChange} readOnly={readOnly} />;
};

export default FormRenderer;
